import React, { useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { Button } from "components/common";
import UnderlineInput, { GEIST_INPUT } from "../common/UnderlineInput";
import UserPicker from "../common/UserPicker";
import { lookupSmartCaCertificates, searchUsers } from "../common/settingsApi";
import { TD, ROW, TONE, Chip } from "./settingsKit";

const shortDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB") : "—");

const STATE_CHIP = {
  ok: [TONE.ok, "bi-patch-check-fill", "Ready"],
  missing: [TONE.warn, "bi-exclamation-circle", "No certificate"],
  expired: [TONE.bad, "bi-x-circle-fill", "Expired"],
  untrusted: [TONE.warn, "bi-shield-exclamation", "Not trusted"],
  invalid: [TONE.bad, "bi-x-circle-fill", "Invalid"],
  not_rsa: [TONE.bad, "bi-x-circle-fill", "Not RSA"],
  serial_mismatch: [TONE.bad, "bi-x-circle-fill", "Serial mismatch"],
};

export const CertificateChip = ({ certificate, dirty }) => {
  if (dirty) return <Chip tone={TONE.muted} icon="bi-pencil">Unsaved — checked on save</Chip>;
  const [tone, icon, label] = STATE_CHIP[certificate?.state] || STATE_CHIP.missing;
  return <Chip tone={tone} icon={icon} title={certificate?.message}>{label}</Chip>;
};

const certificateShape = PropTypes.shape({
  state: PropTypes.string,
  message: PropTypes.string,
  subject: PropTypes.string,
  valid_to: PropTypes.string,
});

CertificateChip.propTypes = {
  certificate: certificateShape,
  dirty: PropTypes.bool,
};

const TEXTAREA = `${GEIST_INPUT} tw-h-auto tw-py-2 tw-font-mono tw-text-[11px] tw-leading-snug`;

/**
 * Một người ký SmartCA. Cách nhanh: nhập SmartCA account ID (thường là số CCCD) →
 * "Get from SmartCA" → Portal đọc chứng thư từ VNPT, điền serial + PEM (và CA nếu
 * chưa có). Nhập tay vẫn được qua "Enter manually".
 */
const SignerCertificateRow = ({ row, onChange, onRemove, onCaFound }) => {
  const [busy, setBusy] = useState(false);
  const [choices, setChoices] = useState([]);
  const [manual, setManual] = useState(false);
  const cert = row.certificate || {};
  const set = (patch) => onChange({ ...patch, dirty: true });

  const applyCertificate = (c) => {
    set({ serial_number: c.serial_number, certificate_pem: c.certificate_pem,
      certificate: { state: "ok", subject: c.subject, valid_to: c.valid_to } });
    if (c.ca_pem) onCaFound(c.ca_pem);
    setChoices([]);
  };

  const fetchFromSmartCa = async () => {
    setBusy(true);
    try {
      const list = ((await lookupSmartCaCertificates(row.user_id)).data || []).filter((c) => c.certificate_pem);
      if (list.length === 0) toast.warn("SmartCA returned no usable certificate for this account.");
      else if (list.length === 1) { applyCertificate(list[0]); toast.success("Certificate loaded from SmartCA. Click Save."); }
      else setChoices(list);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Could not read certificates from SmartCA.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr className={`${ROW} tw-align-top ${row.dirty ? "tw-bg-geist-accent/5" : ""}`}>
      <td className={`${TD} tw-align-top tw-w-[24%] tw-min-w-[200px]`}>
        {row.required ? (
          <>
            <div className="tw-text-sm tw-font-semibold tw-text-geist-fg">{row.name}</div>
            <div className="tw-mt-1 tw-flex tw-flex-wrap tw-gap-1">
              {row.roles.map((r) => <Chip key={r} tone={TONE.muted}>{r}</Chip>)}
            </div>
          </>
        ) : (
          <UserPicker aria-label="Signer" fetcher={searchUsers}
            value={row.portal_user_id ? { id: row.portal_user_id, name: row.name } : null}
            onChange={(u) => set({ portal_user_id: u?.id, name: u?.name })} />
        )}
      </td>
      <td className={`${TD} tw-align-top tw-w-[30%] tw-min-w-[240px]`}>
        <div className="tw-flex tw-gap-2">
          <UnderlineInput aria-label={`SmartCA account of ${row.name || "signer"}`} placeholder="SmartCA account ID (e.g. CCCD)"
            value={row.user_id || ""} onChange={(e) => set({ user_id: e.target.value.trim() })} />
          <Button variant="secondary" size="sm" disabled={busy || !row.user_id} onClick={fetchFromSmartCa}
            title="Read this account's certificate from VNPT SmartCA">
            {busy ? "Reading..." : "Get from SmartCA"}
          </Button>
        </div>
        {choices.length > 0 && (
          <div className="tw-mt-2 tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-overflow-hidden">
            <div className="tw-px-3 tw-py-1.5 tw-text-[11px] tw-font-semibold tw-text-geist-fg-3">This account has {choices.length} certificates — pick one:</div>
            {choices.map((c) => (
              <button key={c.serial_number} type="button" onClick={() => applyCertificate(c)}
                className="tw-block tw-w-full tw-border-0 tw-border-t tw-border-solid tw-border-geist-border tw-bg-transparent tw-px-3 tw-py-2 tw-text-left tw-text-[12px] hover:tw-bg-geist-100">
                <span className="tw-font-semibold">{c.subject || c.serial_number}</span>
                <span className="tw-text-geist-fg-3"> · serial {c.serial_number} · until {shortDate(c.valid_to)}{c.status ? ` · ${c.status}` : ""}</span>
              </button>
            ))}
          </div>
        )}
      </td>
      <td className={`${TD} tw-align-top`}>
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          <CertificateChip certificate={cert} dirty={row.dirty && cert.state !== "ok"} />
          {cert.subject && <span className="tw-text-[12px] tw-text-geist-fg-2">{cert.subject}</span>}
        </div>
        <div className="tw-mt-1 tw-text-[11px] tw-text-geist-fg-3">
          {row.serial_number ? `Serial ${row.serial_number}` : "No serial number"}
          {cert.valid_to ? ` · valid until ${shortDate(cert.valid_to)}` : ""}
          {!row.dirty && cert.state !== "ok" && cert.message ? ` · ${cert.message}` : ""}
        </div>
        <button type="button" onClick={() => setManual((v) => !v)}
          className="tw-mt-1 tw-border-0 tw-bg-transparent tw-p-0 tw-text-[11px] tw-font-medium tw-text-geist-accent hover:tw-underline">
          {manual ? "Hide manual entry" : "Enter manually"}
        </button>
        {manual && (
          <div className="tw-mt-2 tw-space-y-2">
            <UnderlineInput aria-label="Certificate serial number" placeholder="Certificate serial number (hex)"
              value={row.serial_number || ""} onChange={(e) => set({ serial_number: e.target.value.trim() })} />
            <textarea aria-label="Public certificate (PEM)" rows={4} className={TEXTAREA}
              placeholder={"-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----"}
              value={row.certificate_pem || ""} onChange={(e) => set({ certificate_pem: e.target.value })} />
          </div>
        )}
      </td>
      <td className={`${TD} tw-align-top tw-w-10 tw-text-right`}>
        {(!row.required || row.user_id || row.certificate_pem) && (
          <button type="button" aria-label={`Remove ${row.name || "signer"}`} onClick={onRemove}
            title={row.required ? "Clear this person's SmartCA account" : "Remove this signer"}
            className="tw-border-0 tw-bg-transparent tw-p-1 tw-text-geist-fg-3 hover:tw-text-[#8e2a18]">
            <i className="bi bi-trash3" />
          </button>
        )}
      </td>
    </tr>
  );
};

const idType = PropTypes.oneOfType([PropTypes.number, PropTypes.string]);

SignerCertificateRow.propTypes = {
  row: PropTypes.shape({
    name: PropTypes.string,
    // Required signers (from the approval roles) list their roles and cannot be swapped.
    required: PropTypes.bool,
    roles: PropTypes.arrayOf(PropTypes.string),
    portal_user_id: idType,
    user_id: PropTypes.string,
    serial_number: PropTypes.string,
    certificate_pem: PropTypes.string,
    certificate: certificateShape,
    dirty: PropTypes.bool,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onCaFound: PropTypes.func.isRequired,
};

export default SignerCertificateRow;
